import { useCompanyDetail } from './useCompanyDetail';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { mqMin } from 'utils/responsive';
import { RequestMeeting } from 'ui/components/agent/screens/modals/RequestMeeting/RequestMeeting';
import { NeedProfileRegistration } from 'ui/components/agent/screens/modals/NeedProfileRegistration/NeedProfileRegistration';
import { PublicCompanyDetail } from 'ui/components/common/patterns/PublicCompanyDetail/PublicCompanyDetail';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import styled from '@emotion/styled';
import { PROFILE_STAGE } from 'domain/entities/AgentUser/AgentUser';
import { MATCH_STATUSES } from 'domain/entities/Match/Match';
import { useEffect, useRef, useState } from 'react';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: left;
    background-color: #f8f9fd;
    min-height: 100svh;

    ${mqMin('NARROW')} {
        min-height: calc(100vh - var(--height-pc-header));
    }
`;

const DisabledText = styled.div`
    color: #ff4500;
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    text-align: center;
`;

const FixedOfferOverlay = styled.div<{ isVisible: boolean }>`
    position: fixed;
    bottom: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.5);
    border-top: 1px solid #e0e0e0;
    padding: 16px;
    z-index: 1000;
    opacity: ${(props) => (props.isVisible ? '1' : '0')};
    visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    width: 100%;

    ${mqMin('SP')} {
        padding: 20px;
        width: calc(100% - var(--width-pc-side-menu));
    }
`;

const OverlayButtonContainer = styled.div`
    display: flex;
    justify-content: center;

    button {
        max-width: 400px;
        width: 100%;
    }

    ${mqMin('NARROW')} {
        button {
            width: initial;
        }
    }
`;

export const CompanyDetail: React.FC = () => {
    const {
        user,
        loading,
        company,
        setValue,
        watch,
        submit,
        errors,
        sendRequestModal,
        needProfileModal,
        showSnackbar,
        setShowSnackbar,
        areaChoices,
        prefectureChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
    } = useCompanyDetail();
    const [isFixedButtonVisible, setIsFixedButtonVisible] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { openModal, createModal } = sendRequestModal;
    const { openModal: openNeedProfileModal, createModal: createNeedProfileModal } = needProfileModal;

    useEffect(() => {
        const updateVisibility = () => {
            if (wrapperRef.current) {
                const scrollTop = wrapperRef.current.scrollTop;
                const scrollHeight = wrapperRef.current.scrollHeight;
                const clientHeight = wrapperRef.current.clientHeight;

                const threshold = scrollHeight - clientHeight - 100;
                setIsFixedButtonVisible(scrollTop > threshold);
            }
        };

        const wrapperElement = wrapperRef.current;
        if (wrapperElement) {
            wrapperElement.addEventListener('scroll', updateVisibility);
            updateVisibility();

            return () => wrapperElement.removeEventListener('scroll', updateVisibility);
        }
    }, [company]);

    if (!company || !user) return null;

    const user_registration_complete = user.account_profile_stage === PROFILE_STAGE.COMPLETE;

    const onClickRequestButton = () => {
        if (!user_registration_complete) {
            openNeedProfileModal();
        } else {
            openModal();
        }
    };

    return (
        <Wrapper ref={wrapperRef}>
            <SnackBar message="オファーを送信しました！" open={showSnackbar} setOpen={setShowSnackbar} />
            {createModal(
                <RequestMeeting
                    onClick={submit}
                    setValue={(value) => setValue('message', value)}
                    premiumOffer={watch('premium_offer')}
                    setPremiumOffer={(value) => setValue('premium_offer', value)}
                    errorMsg={errors.message?.message || ''}
                />,
            )}
            {createNeedProfileModal(<NeedProfileRegistration profile_step={user.account_profile_stage} />)}
            {!loading && (
                <PublicCompanyDetail
                    company={company}
                    matchStatus={company.match_status || undefined}
                    areaChoices={areaChoices}
                    prefectureChoices={prefectureChoices}
                    employeeChoices={employeeChoices}
                    revenueChoices={revenueChoices}
                    profitLevelChoices={profitLevelChoices}
                    netAssetsLevelChoices={netAssetsLevelChoices}
                    industryChoices={industryChoices}
                />
            )}
            <FixedOfferOverlay isVisible={isFixedButtonVisible}>
                <OverlayButtonContainer>
                    {company?.match_status ? (
                        <DisabledText>
                            {company.match_status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT &&
                                '※オファー送信済みです'}
                            {company.match_status === MATCH_STATUSES.MATCH_STATUS_CONNECTED && '※マッチング済みです'}
                            {company.match_status === MATCH_STATUSES.MATCH_STATUS_EXPIRED && '※オファー期限切れです'}
                            {company.match_status === MATCH_STATUSES.MATCH_STATUS_DECLINED &&
                                '※お相手がオファーを辞退しました'}
                        </DisabledText>
                    ) : (
                        <ActionButton
                            disabled={loading}
                            onClick={onClickRequestButton}
                            bgColor="primary"
                            style={{
                                maxWidth: '400px',
                            }}
                        >
                            オファーを送信する
                        </ActionButton>
                    )}
                </OverlayButtonContainer>
            </FixedOfferOverlay>
        </Wrapper>
    );
};
